/**
 * Navigation Commands Implementation for UI-Model-1
 *
 * Handles ADD_GROUP, ADD_PAGE, UPDATE_GROUP, UPDATE_PAGE, UPDATE_NAVIGATION_CONFIG
 * commands for navigation management.
 * Works directly with API format state.
 */

import { UIModelState, NavigationConfigAPI, ContainerAPI, DirtySection } from '../../types/state.types';
import {
  AddGroupCommand,
  AddPageCommand,
  UpdateGroupCommand,
  UpdatePageCommand,
  UpdateNavigationConfigCommand,
  RemoveGroupCommand,
  RemovePageCommand,
  ReorderGroupsCommand,
  ReorderPagesCommand,
  MovePageBetweenGroupsCommand,
  DuplicatePageCommand,
} from '../../types/command.types';
import { CanvasAPI, GroupAPI, PageAPI } from '../../types/api.types';

// ============================================================================
// Internal Helpers — reduce deeply nested immutable-update boilerplate
// ============================================================================

type ModelLayout = NonNullable<UIModelState['model']>['layout'];
type NavigationType = ModelLayout['navigation'];

/** Build new state with updated navigation groups (no canvas change) */
const withUpdatedGroups = (
  state: UIModelState,
  navigation: NavigationType,
  groups: GroupAPI[]
): UIModelState => ({
  ...state,
  model: {
    ...state.model!,
    layout: {
      ...state.model!.layout,
      navigation: { ...navigation, groups },
    },
  },
});

/** Build new state with updated navigation groups and canvas map */
const withUpdatedGroupsAndCanvas = (
  state: UIModelState,
  navigation: NavigationType,
  groups: GroupAPI[],
  canvas: ModelLayout['canvas']
): UIModelState => ({
  ...state,
  model: {
    ...state.model!,
    layout: {
      ...state.model!.layout,
      navigation: { ...navigation, groups },
      canvas,
    },
  },
});

/** Build new state with a fully replaced navigation object */
const withUpdatedNavigation = (
  state: UIModelState,
  navigation: NavigationType
): UIModelState => ({
  ...state,
  model: {
    ...state.model!,
    layout: {
      ...state.model!.layout,
      navigation,
    },
  },
});

/** Mark a canvas page as dirty so CanvasAutoSave persists it */
const markCanvasDirty = (
  dirtyTracker: UIModelState['dirtyTracker'],
  canvasId: string
): UIModelState['dirtyTracker'] => {
  const newSections = new Set(Array.from(dirtyTracker.sections));
  newSections.add('canvas' as DirtySection);
  const newCanvasPages = new Set(Array.from(dirtyTracker.canvasPages));
  newCanvasPages.add(canvasId);
  return { ...dirtyTracker, sections: newSections, canvasPages: newCanvasPages };
};

/** Find a group by ID. Warns and returns null if not found. */
const findGroupOrBail = (
  navigation: NavigationType,
  groupId: string,
  callerName: string
): { groupIndex: number; group: GroupAPI } | null => {
  const groupIndex = navigation.groups.findIndex((g) => g.id === groupId);
  if (groupIndex === -1) {
    console.warn(`${callerName}: Group ${groupId} not found`);
    return null;
  }
  return { groupIndex, group: navigation.groups[groupIndex] };
};

/** Find a page within a group by ID. Warns and returns null if not found. */
const findPageOrBail = (
  group: GroupAPI,
  pageId: string,
  callerName: string
): { pageIndex: number; page: PageAPI } | null => {
  const pageIndex = group.pages.findIndex((p) => p.id === pageId);
  if (pageIndex === -1) {
    console.warn(`${callerName}: Page ${pageId} not found in group ${group.id}`);
    return null;
  }
  return { pageIndex, page: group.pages[pageIndex] };
};

/** Generate a unique copy name like "Name (Copy)", "Name (Copy 2)", etc. */
const generateUniqueCopyName = (existingNames: Set<string>, baseName: string): string => {
  let copyName = `${baseName} (Copy)`;
  let counter = 2;
  while (existingNames.has(copyName)) {
    copyName = `${baseName} (Copy ${counter})`;
    counter++;
  }
  return copyName;
};

// ============================================================================
// Clone helpers for deep-copying containers with fresh IDs
// ============================================================================

const generateNewId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const cloneContainersWithNewIds = (containers: ContainerAPI[]): ContainerAPI[] => {
  return containers.map((container) => {
    const newContainerId = generateNewId('container');
    return {
      ...container,
      id: newContainerId,
      content: container.content
        ? {
            ...container.content,
            items: container.content.items?.map((item) => {
              if ('type' in item) {
                return { ...item, id: generateNewId('item') };
              }
              return item;
            }),
            nestedContainers: container.content.nestedContainers
              ? cloneContainersWithNewIds(container.content.nestedContainers)
              : undefined,
          }
        : { items: [] },
      metadata: container.metadata
        ? {
            ...container.metadata,
            parentContainerId: container.metadata.parentContainerId
              ? newContainerId
              : undefined,
          }
        : undefined,
    };
  });
};

/**
 * Clone containers keeping only the shell structure (no items, no submodel).
 * Used by page duplication — copies container layout but not field content or submodel bindings.
 */
const cloneContainerShellsWithNewIds = (containers: ContainerAPI[]): ContainerAPI[] => {
  return containers.map((container) => {
    const newContainerId = generateNewId('container');
    return {
      ...container,
      id: newContainerId,
      content: {
        items: [],
        nestedContainers: container.content?.nestedContainers
          ? cloneContainerShellsWithNewIds(container.content.nestedContainers)
          : undefined,
      },
      metadata: container.metadata
        ? {
            ...container.metadata,
            parentContainerId: container.metadata.parentContainerId
              ? newContainerId
              : undefined,
            isOrphaned: undefined,
            orphanedSubmodelName: undefined,
          }
        : undefined,
    };
  });
};

// ============================================================================
// Add Group Command
// ============================================================================

/**
 * Executes ADD_GROUP command
 * Adds a new group to navigation.groups[] and creates canvases for its pages
 */
export const executeAddGroup = (
  state: UIModelState,
  command: AddGroupCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeAddGroup: No model loaded');
    return state;
  }

  const { group, insertIndex } = command.payload;
  const navigation = state.model.layout.navigation;
  const groups = [...navigation.groups];

  // Insert at specified index or append
  if (insertIndex !== undefined && insertIndex >= 0 && insertIndex <= groups.length) {
    groups.splice(insertIndex, 0, group);
  } else {
    groups.push(group);
  }

  // Create canvases for all pages in the group
  const newCanvases: Record<string, CanvasAPI> = {};
  group.pages.forEach((page) => {
    newCanvases[page.canvasId] = {
      pageId: page.id,
      groupId: group.id,
      containers: [],
    };
  });

  const newState = withUpdatedGroupsAndCanvas(state, navigation, groups, {
    ...state.model.layout.canvas,
    ...newCanvases,
  });

  // Mark all new canvases dirty so CanvasAutoSave persists them
  let tracker = newState.dirtyTracker;
  group.pages.forEach((page) => {
    tracker = markCanvasDirty(tracker, page.canvasId);
  });
  return { ...newState, dirtyTracker: tracker };
};

/**
 * Undoes ADD_GROUP command
 * Removes the group from navigation.groups[] and removes its canvases
 */
export const undoAddGroup = (
  state: UIModelState,
  command: AddGroupCommand
): UIModelState => {
  if (!state.model) {
    console.warn('undoAddGroup: No model loaded');
    return state;
  }

  const { group } = command.payload;
  const navigation = state.model.layout.navigation;

  // Remove the group
  const groups = navigation.groups.filter((g) => g.id !== group.id);

  // Remove canvases for all pages in the group
  const canvasMap = { ...state.model.layout.canvas };
  group.pages.forEach((page) => {
    delete canvasMap[page.canvasId];
  });

  return withUpdatedGroupsAndCanvas(state, navigation, groups, canvasMap);
};

// ============================================================================
// Add Page Command
// ============================================================================

/**
 * Executes ADD_PAGE command
 * Adds a new page to a group and creates its canvas
 */
export const executeAddPage = (
  state: UIModelState,
  command: AddPageCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeAddPage: No model loaded');
    return state;
  }

  const { page, groupId, insertIndex } = command.payload;
  const navigation = state.model.layout.navigation;

  // Find the target group
  const found = findGroupOrBail(navigation, groupId, 'executeAddPage');
  if (!found) return state;
  const { groupIndex, group } = found;

  const pages = [...group.pages];

  // Insert at specified index or append
  if (insertIndex !== undefined && insertIndex >= 0 && insertIndex <= pages.length) {
    pages.splice(insertIndex, 0, page);
  } else {
    pages.push(page);
  }

  // Update the group with new pages
  const groups = [...navigation.groups];
  groups[groupIndex] = {
    ...group,
    pages,
  };

  // Create canvas for the new page
  const newCanvas: CanvasAPI = {
    pageId: page.id,
    groupId: groupId,
    containers: [],
  };

  const newState = withUpdatedGroupsAndCanvas(state, navigation, groups, {
    ...state.model.layout.canvas,
    [page.canvasId]: newCanvas,
  });

  // Mark new canvas dirty so CanvasAutoSave persists it
  return { ...newState, dirtyTracker: markCanvasDirty(newState.dirtyTracker, page.canvasId) };
};

/**
 * Undoes ADD_PAGE command
 * Removes the page from the group and removes its canvas
 */
export const undoAddPage = (
  state: UIModelState,
  command: AddPageCommand
): UIModelState => {
  if (!state.model) {
    console.warn('undoAddPage: No model loaded');
    return state;
  }

  const { page, groupId } = command.payload;
  const navigation = state.model.layout.navigation;

  // Find the target group
  const found = findGroupOrBail(navigation, groupId, 'undoAddPage');
  if (!found) return state;
  const { groupIndex, group } = found;

  // Remove the page
  const pages = group.pages.filter((p) => p.id !== page.id);

  // Update the group
  const groups = [...navigation.groups];
  groups[groupIndex] = {
    ...group,
    pages,
  };

  // Remove the canvas
  const canvasMap = { ...state.model.layout.canvas };
  delete canvasMap[page.canvasId];

  return withUpdatedGroupsAndCanvas(state, navigation, groups, canvasMap);
};

// ============================================================================
// Update Group Command
// ============================================================================

/**
 * Executes UPDATE_GROUP command
 * Updates a group's properties (name, configuration)
 */
export const executeUpdateGroup = (
  state: UIModelState,
  command: UpdateGroupCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeUpdateGroup: No model loaded');
    return state;
  }

  const { groupId, updates } = command.payload;
  const navigation = state.model.layout.navigation;

  // Find the target group
  const found = findGroupOrBail(navigation, groupId, 'executeUpdateGroup');
  if (!found) return state;
  const { groupIndex, group } = found;

  // Store previous values for undo (mutate command payload)
  command.payload.previousValues = {
    name: group.name,
    orientation: group.orientation,
    configuration: group.configuration ? { ...group.configuration } : undefined,
  };

  // Apply updates
  const updatedGroup: GroupAPI = {
    ...group,
    ...updates,
    configuration: updates.configuration
      ? { ...group.configuration, ...updates.configuration }
      : group.configuration,
  };

  // If name is updated, also update configuration.name
  if (updates.name && updatedGroup.configuration) {
    updatedGroup.configuration = {
      ...updatedGroup.configuration,
      name: updates.name,
    };
  }

  // Update the groups array
  const groups = [...navigation.groups];
  groups[groupIndex] = updatedGroup;

  return withUpdatedGroups(state, navigation, groups);
};

/**
 * Undoes UPDATE_GROUP command
 * Restores the group's previous properties
 */
export const undoUpdateGroup = (
  state: UIModelState,
  command: UpdateGroupCommand
): UIModelState => {
  if (!state.model) {
    console.warn('undoUpdateGroup: No model loaded');
    return state;
  }

  const { groupId, previousValues } = command.payload;
  if (!previousValues) {
    console.warn('undoUpdateGroup: No previous values stored');
    return state;
  }

  const navigation = state.model.layout.navigation;

  // Find the target group
  const found = findGroupOrBail(navigation, groupId, 'undoUpdateGroup');
  if (!found) return state;
  const { groupIndex, group } = found;

  // Restore previous values
  const restoredGroup: GroupAPI = {
    ...group,
    ...previousValues,
  };

  // Update the groups array
  const groups = [...navigation.groups];
  groups[groupIndex] = restoredGroup;

  return withUpdatedGroups(state, navigation, groups);
};

// ============================================================================
// Update Page Command
// ============================================================================

/**
 * Executes UPDATE_PAGE command
 * Updates a page's properties (name, configuration)
 */
export const executeUpdatePage = (
  state: UIModelState,
  command: UpdatePageCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeUpdatePage: No model loaded');
    return state;
  }

  const { pageId, groupId, updates } = command.payload;
  const navigation = state.model.layout.navigation;

  // Find the target group
  const groupFound = findGroupOrBail(navigation, groupId, 'executeUpdatePage');
  if (!groupFound) return state;
  const { groupIndex, group } = groupFound;

  // Find the target page
  const pageFound = findPageOrBail(group, pageId, 'executeUpdatePage');
  if (!pageFound) return state;
  const { pageIndex, page } = pageFound;

  // Store previous values for undo (mutate command payload)
  command.payload.previousValues = {
    name: page.name,
    configuration: page.configuration ? { ...page.configuration } : undefined,
  };

  // Apply updates
  const updatedPage: PageAPI = {
    ...page,
    ...updates,
    configuration: updates.configuration
      ? { ...page.configuration, ...updates.configuration }
      : page.configuration,
  };

  // If name is updated, also update configuration.name
  if (updates.name && updatedPage.configuration) {
    updatedPage.configuration = {
      ...updatedPage.configuration,
      name: updates.name,
    };
  }

  // Update the pages array
  const pages = [...group.pages];
  pages[pageIndex] = updatedPage;

  // Update the group
  const groups = [...navigation.groups];
  groups[groupIndex] = {
    ...group,
    pages,
  };

  return withUpdatedGroups(state, navigation, groups);
};

/**
 * Undoes UPDATE_PAGE command
 * Restores the page's previous properties
 */
export const undoUpdatePage = (
  state: UIModelState,
  command: UpdatePageCommand
): UIModelState => {
  if (!state.model) {
    console.warn('undoUpdatePage: No model loaded');
    return state;
  }

  const { pageId, groupId, previousValues } = command.payload;
  if (!previousValues) {
    console.warn('undoUpdatePage: No previous values stored');
    return state;
  }

  const navigation = state.model.layout.navigation;

  // Find the target group
  const groupFound = findGroupOrBail(navigation, groupId, 'undoUpdatePage');
  if (!groupFound) return state;
  const { groupIndex, group } = groupFound;

  // Find the target page
  const pageFound = findPageOrBail(group, pageId, 'undoUpdatePage');
  if (!pageFound) return state;
  const { pageIndex, page } = pageFound;

  // Restore previous values
  const restoredPage: PageAPI = {
    ...page,
    ...previousValues,
  };

  // Update the pages array
  const pages = [...group.pages];
  pages[pageIndex] = restoredPage;

  // Update the group
  const groups = [...navigation.groups];
  groups[groupIndex] = {
    ...group,
    pages,
  };

  return withUpdatedGroups(state, navigation, groups);
};

// ============================================================================
// Update Navigation Config Command
// ============================================================================

/**
 * Executes UPDATE_NAVIGATION_CONFIG command
 * Updates the navigation configuration properties
 */
export const executeUpdateNavigationConfig = (
  state: UIModelState,
  command: UpdateNavigationConfigCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeUpdateNavigationConfig: No model loaded');
    return state;
  }

  const { updates } = command.payload;
  const navigation = state.model.layout.navigation;
  const currentConfig = navigation.configuration || {};

  // Store previous values for undo (mutate command payload)
  // Get values from configuration object or from root-level navigation props
  const previousValues: Partial<NavigationConfigAPI> = {};
  const keys = Object.keys(updates) as Array<keyof NavigationConfigAPI>;
  const navRecord = navigation as unknown as Record<string, unknown>;
  keys.forEach((k) => {
    const configValue = currentConfig[k];
    const navValue = navRecord[k];
    (previousValues as Record<string, unknown>)[k] = configValue !== undefined ? configValue : navValue;
  });
  command.payload.previousValues = previousValues;

  // Apply updates to root-level props only (API-Direct architecture)
  // Do NOT create nested configuration object - API expects flat structure
  return withUpdatedNavigation(state, { ...navigation, ...updates });
};

/**
 * Undoes UPDATE_NAVIGATION_CONFIG command
 * Restores the navigation configuration's previous properties
 */
export const undoUpdateNavigationConfig = (
  state: UIModelState,
  command: UpdateNavigationConfigCommand
): UIModelState => {
  if (!state.model) {
    console.warn('undoUpdateNavigationConfig: No model loaded');
    return state;
  }

  const { previousValues } = command.payload;
  if (!previousValues) {
    console.warn('undoUpdateNavigationConfig: No previous values stored');
    return state;
  }

  const navigation = state.model.layout.navigation;

  // Restore previous values to root-level props only (API-Direct architecture)
  return withUpdatedNavigation(state, { ...navigation, ...previousValues });
};

// ============================================================================
// Remove Group Command
// ============================================================================

/**
 * Executes REMOVE_GROUP command
 * Removes a group and all its pages/canvases from navigation
 */
export const executeRemoveGroup = (
  state: UIModelState,
  command: RemoveGroupCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeRemoveGroup: No model loaded');
    return state;
  }

  const { groupId } = command.payload;
  const navigation = state.model.layout.navigation;

  // Find and store the group for undo
  const removedGroup = navigation.groups.find((g) => g.id === groupId);
  if (!removedGroup) {
    console.warn(`executeRemoveGroup: Group ${groupId} not found`);
    return state;
  }

  // Store for undo
  command.payload.removedGroup = removedGroup;

  // Store canvases for undo
  const removedCanvases: Record<string, ContainerAPI[]> = {};
  removedGroup.pages.forEach((page) => {
    const canvas = state.model!.layout.canvas[page.canvasId];
    if (canvas) {
      removedCanvases[page.canvasId] = canvas.containers || [];
    }
  });
  command.payload.removedCanvases = removedCanvases;

  // Remove the group
  const groups = navigation.groups.filter((g) => g.id !== groupId);

  // Update order for remaining groups
  const reorderedGroups = groups.map((g, index) => ({ ...g, order: index }));

  // Remove canvases
  const canvasMap = { ...state.model.layout.canvas };
  removedGroup.pages.forEach((page) => {
    delete canvasMap[page.canvasId];
  });

  return withUpdatedGroupsAndCanvas(state, navigation, reorderedGroups, canvasMap);
};

/**
 * Undoes REMOVE_GROUP command
 * Restores the group and its canvases
 */
export const undoRemoveGroup = (
  state: UIModelState,
  command: RemoveGroupCommand
): UIModelState => {
  if (!state.model || !command.payload.removedGroup) {
    console.warn('undoRemoveGroup: No model or removed group data');
    return state;
  }

  const { removedGroup, removedCanvases } = command.payload;
  const navigation = state.model.layout.navigation;

  // Restore the group at its original order position
  const groups = [...navigation.groups];
  groups.splice(removedGroup.order, 0, removedGroup);

  // Re-order groups
  const reorderedGroups = groups.map((g, index) => ({ ...g, order: index }));

  // Restore canvases
  const canvasMap = { ...state.model.layout.canvas };
  if (removedCanvases) {
    removedGroup.pages.forEach((page) => {
      canvasMap[page.canvasId] = {
        pageId: page.id,
        groupId: removedGroup.id,
        containers: removedCanvases[page.canvasId] || [],
      };
    });
  }

  return withUpdatedGroupsAndCanvas(state, navigation, reorderedGroups, canvasMap);
};

// ============================================================================
// Remove Page Command
// ============================================================================

/**
 * Executes REMOVE_PAGE command
 * Removes a page from a group and removes its canvas
 */
export const executeRemovePage = (
  state: UIModelState,
  command: RemovePageCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeRemovePage: No model loaded');
    return state;
  }

  const { pageId, groupId } = command.payload;
  const navigation = state.model.layout.navigation;

  const groupFound = findGroupOrBail(navigation, groupId, 'executeRemovePage');
  if (!groupFound) return state;
  const { groupIndex, group } = groupFound;

  const pageFound = findPageOrBail(group, pageId, 'executeRemovePage');
  if (!pageFound) return state;
  const { page: removedPage } = pageFound;

  // Store for undo
  command.payload.removedPage = removedPage;
  command.payload.removedCanvas = state.model.layout.canvas[removedPage.canvasId]?.containers || [];

  // Remove the page
  const pages = group.pages.filter((p) => p.id !== pageId);

  // Update order for remaining pages
  const reorderedPages = pages.map((p, index) => ({ ...p, order: index }));

  const groups = [...navigation.groups];
  groups[groupIndex] = { ...group, pages: reorderedPages };

  // Remove canvas
  const canvasMap = { ...state.model.layout.canvas };
  delete canvasMap[removedPage.canvasId];

  return withUpdatedGroupsAndCanvas(state, navigation, groups, canvasMap);
};

/**
 * Undoes REMOVE_PAGE command
 * Restores the page and its canvas
 */
export const undoRemovePage = (
  state: UIModelState,
  command: RemovePageCommand
): UIModelState => {
  if (!state.model || !command.payload.removedPage) {
    console.warn('undoRemovePage: No model or removed page data');
    return state;
  }

  const { groupId, removedPage, removedCanvas } = command.payload;
  const navigation = state.model.layout.navigation;

  const found = findGroupOrBail(navigation, groupId, 'undoRemovePage');
  if (!found) return state;
  const { groupIndex, group } = found;

  // Restore the page at its original order position
  const pages = [...group.pages];
  pages.splice(removedPage.order, 0, removedPage);

  // Re-order pages
  const reorderedPages = pages.map((p, index) => ({ ...p, order: index }));

  const groups = [...navigation.groups];
  groups[groupIndex] = { ...group, pages: reorderedPages };

  // Restore canvas
  const canvasMap = { ...state.model.layout.canvas };
  canvasMap[removedPage.canvasId] = {
    pageId: removedPage.id,
    groupId,
    containers: removedCanvas || [],
  };

  return withUpdatedGroupsAndCanvas(state, navigation, groups, canvasMap);
};

// ============================================================================
// Reorder Groups Command
// ============================================================================

/**
 * Executes REORDER_GROUPS command
 * Reorders groups based on new groupIds order
 */
export const executeReorderGroups = (
  state: UIModelState,
  command: ReorderGroupsCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeReorderGroups: No model loaded');
    return state;
  }

  const { groupIds } = command.payload;
  const navigation = state.model.layout.navigation;

  // Store previous order for undo
  command.payload.previousOrder = navigation.groups.map((g) => g.id);

  // Reorder groups
  const groupMap = new Map(navigation.groups.map((g) => [g.id, g]));
  const reorderedGroups = groupIds
    .map((id) => groupMap.get(id))
    .filter((g): g is GroupAPI => g !== undefined)
    .map((g, index) => ({ ...g, order: index }));

  return withUpdatedGroups(state, navigation, reorderedGroups);
};

/**
 * Undoes REORDER_GROUPS command
 * Restores previous group order
 */
export const undoReorderGroups = (
  state: UIModelState,
  command: ReorderGroupsCommand
): UIModelState => {
  if (!state.model || !command.payload.previousOrder) {
    console.warn('undoReorderGroups: No model or previous order data');
    return state;
  }

  const { previousOrder } = command.payload;
  const navigation = state.model.layout.navigation;

  const groupMap = new Map(navigation.groups.map((g) => [g.id, g]));
  const restoredGroups = previousOrder
    .map((id) => groupMap.get(id))
    .filter((g): g is GroupAPI => g !== undefined)
    .map((g, index) => ({ ...g, order: index }));

  return withUpdatedGroups(state, navigation, restoredGroups);
};

// ============================================================================
// Reorder Pages Command
// ============================================================================

/**
 * Executes REORDER_PAGES command
 * Reorders pages within a group based on new pageIds order
 */
export const executeReorderPages = (
  state: UIModelState,
  command: ReorderPagesCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeReorderPages: No model loaded');
    return state;
  }

  const { groupId, pageIds } = command.payload;
  const navigation = state.model.layout.navigation;

  const found = findGroupOrBail(navigation, groupId, 'executeReorderPages');
  if (!found) return state;
  const { groupIndex, group } = found;

  // Store previous order for undo
  command.payload.previousOrder = group.pages.map((p) => p.id);

  // Reorder pages
  const pageMap = new Map(group.pages.map((p) => [p.id, p]));
  const reorderedPages = pageIds
    .map((id) => pageMap.get(id))
    .filter((p): p is PageAPI => p !== undefined)
    .map((p, index) => ({ ...p, order: index }));

  const groups = [...navigation.groups];
  groups[groupIndex] = { ...group, pages: reorderedPages };

  return withUpdatedGroups(state, navigation, groups);
};

/**
 * Undoes REORDER_PAGES command
 * Restores previous page order within a group
 */
export const undoReorderPages = (
  state: UIModelState,
  command: ReorderPagesCommand
): UIModelState => {
  if (!state.model || !command.payload.previousOrder) {
    console.warn('undoReorderPages: No model or previous order data');
    return state;
  }

  const { groupId, previousOrder } = command.payload;
  const navigation = state.model.layout.navigation;

  const found = findGroupOrBail(navigation, groupId, 'undoReorderPages');
  if (!found) return state;
  const { groupIndex, group } = found;

  const pageMap = new Map(group.pages.map((p) => [p.id, p]));
  const restoredPages = previousOrder
    .map((id) => pageMap.get(id))
    .filter((p): p is PageAPI => p !== undefined)
    .map((p, index) => ({ ...p, order: index }));

  const groups = [...navigation.groups];
  groups[groupIndex] = { ...group, pages: restoredPages };

  return withUpdatedGroups(state, navigation, groups);
};

// ============================================================================
// Move Page Between Groups Command
// ============================================================================

/**
 * Executes MOVE_PAGE_BETWEEN_GROUPS command
 * Moves a page from one group to another
 */
export const executeMovePageBetweenGroups = (
  state: UIModelState,
  command: MovePageBetweenGroupsCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeMovePageBetweenGroups: No model loaded');
    return state;
  }

  const { pageId, fromGroupId, toGroupId, toIndex } = command.payload;
  const navigation = state.model.layout.navigation;

  const fromGroupIndex = navigation.groups.findIndex((g) => g.id === fromGroupId);
  const toGroupIndex = navigation.groups.findIndex((g) => g.id === toGroupId);

  if (fromGroupIndex === -1 || toGroupIndex === -1) {
    console.warn('executeMovePageBetweenGroups: Source or target group not found');
    return state;
  }

  const fromGroup = navigation.groups[fromGroupIndex];
  const toGroup = navigation.groups[toGroupIndex];

  // Find the page
  const pageIndex = fromGroup.pages.findIndex((p) => p.id === pageId);
  if (pageIndex === -1) {
    console.warn(`executeMovePageBetweenGroups: Page ${pageId} not found in source group`);
    return state;
  }

  const page = fromGroup.pages[pageIndex];

  // Store for undo
  command.payload.fromIndex = pageIndex;

  // Remove from source group
  const fromPages = fromGroup.pages.filter((p) => p.id !== pageId);
  const reorderedFromPages = fromPages.map((p, i) => ({ ...p, order: i }));

  // Add to target group at specified index
  const toPages = [...toGroup.pages];
  toPages.splice(toIndex, 0, page);
  const reorderedToPages = toPages.map((p, i) => ({ ...p, order: i }));

  // Update groups
  const groups = [...navigation.groups];
  groups[fromGroupIndex] = { ...fromGroup, pages: reorderedFromPages };
  groups[toGroupIndex] = { ...toGroup, pages: reorderedToPages };

  // Update canvas groupId
  const canvas = { ...state.model.layout.canvas };
  if (canvas[page.canvasId]) {
    canvas[page.canvasId] = { ...canvas[page.canvasId], groupId: toGroupId };
  }

  return withUpdatedGroupsAndCanvas(state, navigation, groups, canvas);
};

/**
 * Undoes MOVE_PAGE_BETWEEN_GROUPS command
 * Moves the page back to its original group
 */
export const undoMovePageBetweenGroups = (
  state: UIModelState,
  command: MovePageBetweenGroupsCommand
): UIModelState => {
  if (!state.model || command.payload.fromIndex === undefined) {
    console.warn('undoMovePageBetweenGroups: No model or fromIndex data');
    return state;
  }

  // Reverse the move
  const reverseCommand: MovePageBetweenGroupsCommand = {
    ...command,
    payload: {
      pageId: command.payload.pageId,
      fromGroupId: command.payload.toGroupId,
      toGroupId: command.payload.fromGroupId,
      toIndex: command.payload.fromIndex,
    },
  };

  return executeMovePageBetweenGroups(state, reverseCommand);
};

// ============================================================================
// Duplicate Page Command
// ============================================================================

/**
 * Executes DUPLICATE_PAGE command
 * Duplicates a page with all its canvas content
 */
export const executeDuplicatePage = (
  state: UIModelState,
  command: DuplicatePageCommand
): UIModelState => {
  if (!state.model) {
    console.warn('executeDuplicatePage: No model loaded');
    return state;
  }

  const { pageId, groupId, newPageId, newCanvasId } = command.payload;
  const navigation = state.model.layout.navigation;

  const found = findGroupOrBail(navigation, groupId, 'executeDuplicatePage');
  if (!found) return state;
  const { groupIndex, group } = found;

  const originalPage = group.pages.find((p) => p.id === pageId);
  if (!originalPage) {
    console.warn(`executeDuplicatePage: Page ${pageId} not found`);
    return state;
  }

  // Generate unique copy name
  const existingNames = new Set(group.pages.map(p => p.name));
  const copyName = generateUniqueCopyName(existingNames, originalPage.name);

  // Create duplicated page
  const duplicatedPage: PageAPI = {
    ...originalPage,
    id: newPageId,
    canvasId: newCanvasId,
    name: copyName,
    order: group.pages.length,
    configuration: originalPage.configuration
      ? { ...originalPage.configuration, name: copyName }
      : { autoAdvance: false, name: copyName },
  };

  // Store for undo
  command.payload.duplicatedPage = duplicatedPage;

  // Add page to group
  const pages = [...group.pages, duplicatedPage];
  const groups = [...navigation.groups];
  groups[groupIndex] = { ...group, pages };

  // Duplicate canvas with container structure only (no field content inside)
  const originalCanvas = state.model.layout.canvas[originalPage.canvasId];
  const duplicatedCanvas: CanvasAPI = {
    pageId: newPageId,
    groupId: groupId,
    containers: originalCanvas?.containers
      ? cloneContainerShellsWithNewIds(JSON.parse(JSON.stringify(originalCanvas.containers)))
      : [],
  };

  const newState = withUpdatedGroupsAndCanvas(state, navigation, groups, {
    ...state.model.layout.canvas,
    [newCanvasId]: duplicatedCanvas,
  });

  // Mark new canvas dirty so CanvasAutoSave persists it
  return { ...newState, dirtyTracker: markCanvasDirty(newState.dirtyTracker, newCanvasId) };
};

/**
 * Undoes DUPLICATE_PAGE command
 * Removes the duplicated page and its canvas
 */
export const undoDuplicatePage = (
  state: UIModelState,
  command: DuplicatePageCommand
): UIModelState => {
  if (!state.model || !command.payload.duplicatedPage) {
    console.warn('undoDuplicatePage: No model or duplicated page data');
    return state;
  }

  const { groupId, newCanvasId, duplicatedPage } = command.payload;
  const navigation = state.model.layout.navigation;

  const found = findGroupOrBail(navigation, groupId, 'undoDuplicatePage');
  if (!found) return state;
  const { groupIndex, group } = found;

  // Remove the duplicated page
  const pages = group.pages.filter((p) => p.id !== duplicatedPage.id);
  const groups = [...navigation.groups];
  groups[groupIndex] = { ...group, pages };

  // Remove the duplicated canvas
  const canvas = { ...state.model.layout.canvas };
  delete canvas[newCanvasId];

  return withUpdatedGroupsAndCanvas(state, navigation, groups, canvas);
};
